/*eslint-disable*/
import { createContext, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes, { any } from 'prop-types';
import { getConexion, postConexion } from '../conexion/Conexion';
import useAuth from 'src/hooks/useAuth';

const now = new Date();

interface State {
  dashboardMaster: any | null;
  notifications: any | null;
  tradeByBusiness: any | null;
  tradesDetails: any | null;
  tradesDetailsArray: any | null;
  tradeProvider: any | null;
  dashboardProvider: any | null;
  fiAnalytics: any | null;
  dashboardAdmin: any | null;
  allTradesProvider: any | null;
  firstStep: any | null;
}

interface IndicatorsContextValue extends State {
  getDashboardMaster: () => Promise<void>;
  getNotifications: () => Promise<void>;
  getTradeByBusiness: () => Promise<void>;
  getTradesDetail: (name) => Promise<void>;
  getTradesDetailArray: (name) => Promise<void>;
  getTradeProvider: () => Promise<void>;
  getDashboardProvider: (ruc) => Promise<void>;
  getAllTradesProvider: (ruc) => Promise<void>;
  getFiAnalytics: () => Promise<void>;
  getDashboardAdmin: () => Promise<void>;
  getFirstStep: (value) => void;
}

interface IndicatorsProviderProps {
  children: ReactNode;
}

type GetFirstStepAction = {
  type: 'GETFIRSTSTEP';
  payload: {
    firstStep: any;
  };
};

type GetDashboardMasterAction = {
  type: 'GETDASHBOARDMASTER';
  payload: {
    dashboardMaster: any;
  };
};

type GetNotificationsAction = {
  type: 'GETNOTIFICATIONS';
  payload: {
    notifications: any;
  };
};

type GetTradeByBusinessAction = {
  type: 'GETTRADEBYBUSINESS';
  payload: {
    tradeByBusiness: any;
  };
};

type GetTradesDetailAction = {
  type: 'GETTRADESDETAIL';
  payload: {
    tradesDetails: any;
  };
};

type GetTradesDetailArrayAction = {
  type: 'GETTRADESDETAILARRAY';
  payload: {
    tradesDetailsArray: any;
  };
};

type GetTradeProviderAction = {
  type: 'GETTRADEPROVIDER';
  payload: {
    tradeProvider: any;
  };
};

type GetDashboardProviderAction = {
  type: 'GETDASHBOARDPROVIDER';
  payload: {
    dashboardProvider: any;
  };
};

type GetAllTradesProviderAction = {
  type: 'GETALLTRADESPROVIDER';
  payload: {
    allTradesProvider: any;
  };
};

type GetFiAnalyticsAction = {
  type: 'GETFIANALYTICS';
  payload: {
    fiAnalytics: any;
  };
};

type GetDashboardAdminAction = {
  type: 'GETDASHBOARDADMIN';
  payload: {
    dashboardAdmin: any;
  };
};

type Action =
  | GetDashboardMasterAction
  | GetNotificationsAction
  | GetTradeByBusinessAction
  | GetTradesDetailAction
  | GetTradesDetailArrayAction
  | GetTradeProviderAction
  | GetDashboardProviderAction
  | GetAllTradesProviderAction
  | GetFiAnalyticsAction
  | GetDashboardAdminAction
  | GetFirstStepAction;

const initialState: State = {
  dashboardMaster: null,
  notifications: null,
  tradeByBusiness: [],
  tradesDetails: null,
  tradesDetailsArray: [],
  tradeProvider: null,
  dashboardProvider: null,
  fiAnalytics: null,
  dashboardAdmin: null,
  allTradesProvider: null,
  firstStep: null,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  GETDASHBOARDMASTER: (
    state: State,
    action: GetDashboardMasterAction
  ): State => {
    const { dashboardMaster } = action.payload;

    return {
      ...state,
      dashboardMaster,
    };
  },
  GETNOTIFICATIONS: (state: State, action: GetNotificationsAction): State => {
    const { notifications } = action.payload;

    return {
      ...state,
      notifications,
    };
  },
  GETTRADEBYBUSINESS: (
    state: State,
    action: GetTradeByBusinessAction
  ): State => {
    const { tradeByBusiness } = action.payload;

    return {
      ...state,
      tradeByBusiness,
    };
  },
  GETTRADESDETAIL: (state: State, action: GetTradesDetailAction): State => {
    const { tradesDetails } = action.payload;

    return {
      ...state,
      tradesDetails,
    };
  },
  GETTRADESDETAILARRAY: (state: State, action: GetTradesDetailArrayAction): State => {
    const { tradesDetailsArray } = action.payload;

    return {
      ...state,
      tradesDetailsArray,
    };
  },
  GETTRADEPROVIDER: (state: State, action: GetTradeProviderAction): State => {
    const { tradeProvider } = action.payload;

    return {
      ...state,
      tradeProvider,
    };
  },
  GETDASHBOARDPROVIDER: (
    state: State,
    action: GetDashboardProviderAction
  ): State => {
    const { dashboardProvider } = action.payload;

    return {
      ...state,
      dashboardProvider,
    };
  },
  GETALLTRADESPROVIDER: (
    state: State,
    action: GetAllTradesProviderAction
  ): State => {
    const { allTradesProvider } = action.payload;

    return {
      ...state,
      allTradesProvider,
    };
  },
  GETFIANALYTICS: (state: State, action: GetFiAnalyticsAction): State => {
    const { fiAnalytics } = action.payload;

    return {
      ...state,
      fiAnalytics,
    };
  },
  GETDASHBOARDADMIN: (state: State, action: GetDashboardAdminAction): State => {
    const { dashboardAdmin } = action.payload;

    return {
      ...state,
      dashboardAdmin,
    };
  },
  GETFIRSTSTEP: (state: State, action: GetFirstStepAction): State => {
    const { firstStep } = action.payload;

    return {
      ...state,
      firstStep,
    };
  },
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const IndicatorsContext = createContext<IndicatorsContextValue>({
  ...initialState,
  getDashboardMaster: () => Promise.resolve(),
  getNotifications: () => Promise.resolve(),
  getTradeByBusiness: () => Promise.resolve(),
  getTradesDetail: () => Promise.resolve(),
  getTradesDetailArray: () => Promise.resolve(),
  getTradeProvider: () => Promise.resolve(),
  getDashboardProvider: () => Promise.resolve(),
  getAllTradesProvider: () => Promise.resolve(),
  getFiAnalytics: () => Promise.resolve(),
  getDashboardAdmin: () => Promise.resolve(),
  getFirstStep: () => { },
});

export const IndicatorsProvider: FC<IndicatorsProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user } = useAuth();
  const fecha = `${now.getFullYear()}-${now.getMonth() + 1 < 10 ? `0${now.getMonth() + 1}` : now.getMonth() + 1
    }-${now.getDate() < 10 ? `0${now.getDate()}` : now.getDate()}`;

  const getDashboardMaster = async (): Promise<void> => {
    try {
      if (user['rol'] === 'master' || user['rol'] === 'estandar') {
        const bussinessId = user['businessId']['business_id'];
        const obj = {
          businessId: bussinessId,
          date: fecha,
          valor_total_pagado: {
            step: 15,
          },
        };
        const response = await postConexion('cash/getIfs', obj);

        dispatch({
          type: 'GETDASHBOARDMASTER',
          payload: {
            dashboardMaster: response,
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getNotifications = async (): Promise<void> => {
    try {
      const bussinessId = user['businessId']['business_id'];
      const obj = {
        businessId: bussinessId,
        date: fecha,
      };
      const response = await postConexion('cash/getEtlStatus', obj);

      dispatch({
        type: 'GETNOTIFICATIONS',
        payload: {
          notifications: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getTradeByBusiness = async (): Promise<void> => {
    try {
      const bussinessId = user['businessId']['business_id'];
      const obj = {
        businessId: bussinessId,
        fecha,
      };
      const response = await postConexion('cash/getTradesByBusinessId', obj);

      dispatch({
        type: 'GETTRADEBYBUSINESS',
        payload: {
          tradeByBusiness: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getTradesDetail = async (name): Promise<void> => {
    try {
      const obj = new Object();
      if (user.rol === 'proveedor') {
        obj['businessId'] = name;
        obj['fecha'] = fecha;
        obj['identificacion'] = user.ruc;
      } else {
        obj['fecha'] = fecha;
        obj['identificacion'] = name;
      }
      const response = await postConexion('cash/getDecissionsByRelation', obj);

      dispatch({
        type: 'GETTRADESDETAIL',
        payload: {
          tradesDetails: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getTradesDetailArray = async (name): Promise<void> => {
    try {
      const obj = new Object();
      if (user.rol === 'proveedor') {
        obj['businessId'] = name;
        obj['fecha'] = fecha;
        obj['identificacion'] = user.ruc;
      } else {
        obj['fecha'] = fecha;
        obj['identificacion'] = name;
      }
      const response = await postConexion('cash/getDecissionsByRelationArray', obj);

      dispatch({
        type: 'GETTRADESDETAILARRAY',
        payload: {
          tradesDetailsArray: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getTradeProvider = async (): Promise<void> => {
    try {
      const id = user.ruc;
      const obj = {
        identificacion: id,
        fecha,
      };
      const response = await postConexion('cash/getTradesPerProveedor', obj);

      dispatch({
        type: 'GETTRADEPROVIDER',
        payload: {
          tradeProvider: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getDashboardProvider = async (ruc): Promise<void> => {
    try {
      const obj = {
        ruc: ruc,
      };
      const response = await postConexion('cash/getIfsProv', obj);

      dispatch({
        type: 'GETDASHBOARDPROVIDER',
        payload: {
          dashboardProvider: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getAllTradesProvider = async (ruc): Promise<void> => {
    try {
      const obj = {
        identificacion: ruc,
      };
      const response = await postConexion('cash/getDecissionsByRuc', obj);

      dispatch({
        type: 'GETALLTRADESPROVIDER',
        payload: {
          allTradesProvider: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getFiAnalytics = async (): Promise<void> => {
    try {
      const response = await getConexion('indicadores/getFiAndAnalytics');

      dispatch({
        type: 'GETFIANALYTICS',
        payload: {
          fiAnalytics: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getDashboardAdmin = async (): Promise<void> => {
    try {
      let timeInterval = now.getTime();
      timeInterval = timeInterval - 2208 * 60 * 60000;

      let pastDate = new Date(timeInterval);
      const fechaFinal = `${pastDate.getFullYear()}-${pastDate.getMonth() + 1 < 10
          ? `0${pastDate.getMonth() + 1}`
          : pastDate.getMonth() + 1
        }-${pastDate.getDate() < 10 ? `0${pastDate.getDate()}` : pastDate.getDate()
        }`;

      const obj = {
        date: fecha,
        comportamiento_transaccional_trade: {
          fechaDesde: fechaFinal,
          fechaHasta: fecha,
        },
        desempeno_usuario_vs_sugerencias_sistema: {
          fechaDesde: fechaFinal,
          fechaHasta: fecha,
        },
        rentabilidad_generada_x_num_trx: {
          fechaDesde: fechaFinal,
          fechaHasta: fecha,
        },
      };
      const response = await postConexion('indicadores/getIfs', obj);

      dispatch({
        type: 'GETDASHBOARDADMIN',
        payload: {
          dashboardAdmin: response,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getFirstStep = (value) => {
    try {
      dispatch({
        type: 'GETFIRSTSTEP',
        payload: {
          firstStep: value,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <IndicatorsContext.Provider
      value={{
        ...state,
        getDashboardMaster,
        getNotifications,
        getTradeByBusiness,
        getTradesDetail,
        getTradesDetailArray,
        getTradeProvider,
        getDashboardProvider,
        getAllTradesProvider,
        getFiAnalytics,
        getDashboardAdmin,
        getFirstStep,
      }}
    >
      {children}
    </IndicatorsContext.Provider>
  );
};

IndicatorsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default IndicatorsContext;
